import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';

import { getCart } from 'api/operations/cartServiceV2';
import { getPortfoliosById } from 'api/operations/productServiceV2';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { pickCarriers } from 'api/operations/enrollmentService';
import { InputRFQCarrierByPLine } from 'types/enrollmentService';
import { bpHistory } from 'global/bpHistory';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { PortfoliosByCarrier, SelectedPortfolioPresenter } from './types';

interface DomainParams {
  cartId: string;
  periodId: string;
}

export default class BrowsingReviewCarriersPageStore {
  private _rootStore: RootStore = getRootStoreInstance();
  private _groupId: string = '';
  private _isLoading = false;
  // carrierId => productLineId => PortfoliosByCarrier
  private _carrierProductLinePortfolioMap: Map<string, Map<ProductLineId, PortfoliosByCarrier>> = new Map();

  private _domainParams: DomainParams;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get selectedPortfoliosPresenters(): SelectedPortfolioPresenter[] {
    return Array.from(this._carrierProductLinePortfolioMap.entries()).map(([_, productLinePortfolioMap]) => {
      const portfoliosByProductLine = Array.from(productLinePortfolioMap.entries()).map(
        ([productLineId, portfoliosByCarrier]) => ({
          productLineId,
          portfolioNames: portfoliosByCarrier.portfolios.map((portfolio) => portfolio.name),
        }),
      );

      return {
        carrierLogoUrl: Array.from(productLinePortfolioMap.values())[0]?.carrier.logo || '',
        carrierId: Array.from(productLinePortfolioMap.values())[0]?.carrier.id || '',
        portfoliosByProductLine,
      };
    });
  }

  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);

    const cart = await getCart(this._domainParams.cartId);

    await runInAction(async () => {
      this._groupId = cart.groupId;
      const portfolios = await getPortfoliosById(
        cart.cartItems,
        cart.lastUpdateBrowsingCriteria,
        '',
        this._domainParams.cartId,
      );

      runInAction(() => {
        this._carrierProductLinePortfolioMap = portfolios.reduce((carrierMap, portfolio) => {
          const carrier = portfolio.products[0].carrier;
          const carrierId = carrier.id;

          if (!carrierMap.has(carrierId)) {
            carrierMap.set(carrierId, new Map());
          }

          const productLinePortfolioMap = carrierMap.get(carrierId)!;

          if (!productLinePortfolioMap.has(portfolio.productLineId)) {
            productLinePortfolioMap.set(portfolio.productLineId, {
              carrier,
              portfolios: [],
            });
          }

          const portfoliosByCarrier = productLinePortfolioMap.get(portfolio.productLineId)!;
          portfoliosByCarrier.portfolios.push(portfolio);

          return carrierMap;
        }, new Map<string, Map<ProductLineId, PortfoliosByCarrier>>());

        this.setIsLoading(false);
      });
    });
  }

  public async saveCarriersSelection(): Promise<void> {
    this.setIsLoading(true);
    const inputRfqCarriersByPLine: InputRFQCarrierByPLine[] = Array.from(
      this._carrierProductLinePortfolioMap.entries(),
    ).flatMap(([carrierId, productLinePortfolioMap]) =>
      Array.from(productLinePortfolioMap.entries()).map(([productLineId, portfoliosByCarrier]) => ({
        productLineId,
        carrierId,
        sponsorId: this._rootStore.domainStoreV2.sponsorId,
        primaryRecipient: '',
        ccRecipients: [],
        portfolios: portfoliosByCarrier.portfolios.map(({ id, name }) => ({ id, name })),
      })),
    );
    await pickCarriers(this._domainParams.periodId, this._domainParams.cartId, inputRfqCarriersByPLine);
    runInAction(() => {
      bpHistory.push(`/group/${this._groupId}`);
    });
  }

  constructor(domainParams: DomainParams) {
    this._domainParams = domainParams;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new BrowsingReviewCarriersPageStore({} as DomainParams));

export const useBrowsingReviewCarriersPageStore = (): BrowsingReviewCarriersPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
